import { FC, useContext, useEffect, useRef, useState } from 'react';

import { useConfig } from '@core/contexts/ConfigContext';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import faviconDotImage from '@shared/assets/images/favicons/fav-icon-dot.png';
import { IconButton } from '@shared/components/icon-button';
import { Popover } from '@shared/components/popover';
import { RouteContext } from '@shared/contexts/RouteContext';
import { useEventTracking } from '@shared/hooks/eventTracking.hooks';
import { useNotificationHistory } from '@shared/hooks/useNotificationHistory';
import { useNotifications } from '@shared/hooks/useNotifications';
import { BellIcon } from '@shared/icons/bell';
import { colours } from '@shared/theme/colours';
import { NewButtonColour } from '@shared/types/common/button';
import { NotificationsEventPayloads } from '@shared/types/common/eventTracking';
import { formatCountForBadge } from '@shared/utils/number';

import { NotificationsPanel, NotificationsTab } from './components/notifications-panel';
import { styles } from './NotificationsBell.styles';

export interface NotificationsBellProps extends WithStyles<typeof styles> {
  variant?: 'icon' | 'nav-item';
}

const NotificationsBellComponent: FC<NotificationsBellProps> = ({ classes, variant = 'icon' }) => {
  const anchorRef = useRef<HTMLDivElement>(null);

  const { featuresEnabled } = useConfig();
  const { setTitlePrefix } = useContext(RouteContext);
  const tracking = useEventTracking<NotificationsEventPayloads>({ apiSlug: 'notifications' });

  const {
    handleAction,
    handleDismiss,
    handleItemClick,
    handleRetry,
    handleViewAllInvites,
    isLoading,
    notifications,
    outstandingCount,
  } = useNotifications();

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationsTab>('all');

  // Only starts fetching once the tab is opened - history is a secondary surface, so it
  // should not cost a request every time the bell opens on the default All tab.
  const history = useNotificationHistory(activeTab === 'history');

  const showFaviconDot = featuresEnabled.webapp.in_app_notifications && outstandingCount > 0;

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link || !showFaviconDot) return;

    const originalHref = link.href;
    link.href = faviconDotImage;

    return () => {
      link.href = originalHref;
    };
  }, [showFaviconDot]);

  useEffect(() => {
    if (!showFaviconDot) return;

    setTitlePrefix(outstandingCount.toString());

    return () => {
      setTitlePrefix('');
    };
  }, [outstandingCount, setTitlePrefix, showFaviconDot]);

  if (!featuresEnabled.webapp.in_app_notifications) {
    return null;
  }

  const isNavItem = variant === 'nav-item';

  const handleOpen = () => {
    // Fresh panel on every open, same as before this was lifted out of NotificationsPanel.
    setActiveTab('all');
    setOpen(true);
    tracking.fire('notifications_bell_opened', { object_id: null, object_type: null });
  };

  return (
    <>
      <div ref={anchorRef} className={isNavItem ? classes.navItemRoot : classes.root}>
        {isNavItem ? (
          <button className={classes.navItem} type="button" onClick={handleOpen}>
            <span className={classes.navItemIcon}>
              <BellIcon />
            </span>
            <span className={classes.navItemTitle}>{t('notifications')}</span>
            {outstandingCount > 0 && (
              <span className={classes.navItemCount}>{formatCountForBadge(outstandingCount)}</span>
            )}
          </button>
        ) : (
          <>
            <IconButton
              colour={NewButtonColour.Outline}
              icon={<BellIcon color={colours.neutral[700]} />}
              onClick={handleOpen}
            />
            {outstandingCount > 0 && <span className={classes.outstandingDot} />}
          </>
        )}
      </div>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        className={classes.popoverPaper}
        open={open}
        transformOrigin={
          isNavItem ? { horizontal: 'left', vertical: 'bottom' } : { horizontal: 'right', vertical: 'top' }
        }
        onClose={() => setOpen(false)}
      >
        <NotificationsPanel
          activeTab={activeTab}
          historyHasMore={history.pagination.current_page < history.pagination.total_pages}
          historyIsLoading={history.isLoading}
          historyItems={history.items}
          historyScrollRef={history.scrollRef}
          loading={isLoading}
          notifications={notifications}
          onAction={handleAction}
          onDismiss={handleDismiss}
          onHistoryScroll={history.onScroll}
          onItemClick={(item) => {
            // Close before navigating — otherwise the panel sits over the page it just
            // sent the user to.
            setOpen(false);
            handleItemClick(item);
          }}
          onRetry={handleRetry}
          onTabChange={(tab) => {
            tracking.fireChange('notifications_tab_changed', {
              object_id: null,
              object_type: null,
              change_from: activeTab,
              change_location: 'popover',
              change_to: tab,
              change_type: 'tab',
            });
            setActiveTab(tab);
          }}
          onViewAllInvites={() => {
            tracking.fire('notifications_view_all_invites_clicked', { object_id: null, object_type: null });
            setOpen(false);
            handleViewAllInvites();
          }}
        />
      </Popover>
    </>
  );
};

export const NotificationsBell = withStyles(styles)(NotificationsBellComponent);
